/**
 * Reacting to a card the room has taken away — CR4 of `docs/manual-tests/13-fantasy-walk.md`.
 *
 * <b>The defect this closes.</b> After a room revoked a card, the portal went on showing the
 * holder's name and navigating Home, Fantasy and My Stats until a hard refresh. The server was
 * never wrong: `AuthPolicies.Member` refuses every request, and a reload lands on <i>Connect your
 * player card</i>. Only the client was stale, because `/api/me` is the sole source of memberships
 * and nothing refetched it on a 403. Finding 7 downgrades that from a security hole to a
 * query-invalidation bug — but a room revokes access because somebody should not have it, and
 * "until they refresh" is not a boundary.
 *
 * <b>Everything here is pure, and it imports nothing.</b> The rules below are the two decisions
 * the fix turns on — which failures mean "your card is gone", and what to do about it — and both
 * fail quietly if got wrong: too wide a predicate tears down a working session, too narrow a one
 * leaves the stale portal exactly as it was, and the branch decision's own trap (see
 * `revokeOutcome`) teleports somebody into a room they never chose. So they are testable without a
 * DOM, a query client or a network — `membership.test.js` runs them directly.
 */

/**
 * The minimum of a `Membership` these rules read.
 *
 * Structural rather than the generated `Membership`, so this module resolves no imports at all and
 * Node can strip the types off it and run it under `node --test`. The generic keeps the real DTO's
 * type flowing back out of `resolveMembership`, so nothing widens at the call site.
 */
export interface CardAtRoom {
  readonly roomId: string;
  readonly roomName: string;
}

/** A card this sign-in held that the room has since disconnected, and what to say about it. */
export interface RevokedCard {
  readonly roomId: string;
  readonly roomName: string;
  /** Whether this sign-in still holds a card somewhere else — the notice differs. */
  readonly othersRemain: boolean;
}

// ------------------------------------------------------------------------- the signal

/**
 * The 403s that mean "this caller no longer holds a card here".
 *
 * Two codes and no code, and the third is not an omission. `AuthPolicies.Member` is a *policy*, so
 * it runs before model binding, leaves the requirement unmet and lets ASP.NET answer — a bare 403
 * with no error envelope at all, because a policy has nothing specific to say about a request it
 * never looked at. `room_not_a_member` (`RoomScopeGuard.DeniedRead`) and `membership_required`
 * (`FantasyTeamService`) are the same refusal one layer down, where the room being asked about is
 * known and the answer can name it.
 */
export const MEMBERSHIP_DENIAL_CODES: readonly string[] = [
  'room_not_a_member',
  'membership_required',
];

/**
 * Whether a failed request should send the portal to re-read `/api/me` — the cue to ask whether a
 * card has gone, never the answer. {@link revokeOutcome}'s diff of the membership lists before and
 * after is the answer; a trigger that turns out to be nothing finds nothing gone and says nothing.
 *
 * <b>Three shapes trigger it.</b> The membership 403s below; and <b>any 404</b>, since the tenancy
 * guard answers an entity in a room the caller can no longer read exactly as one that does not
 * exist. A player holding cards at A and B whose card at A was revoked passes the `Member` policy
 * (they still hold B), so clicking A's contest or board answers 404 where it answered
 * `room_not_a_member` — and without this the check would never fire for them. Only a player left
 * holding no card at all still meets the bare policy 403. A 404 that is a real absence costs one
 * `/api/me`, and {@link shouldRecheck} keeps a burst of them to one.
 *
 * <b>Read structurally — status and code, never `instanceof`.</b> `ApiError` carries exactly those
 * two, and reading them off a plain object is what lets this rule be tested without pulling
 * `@brush/api-client` into a `node --test` process. Nothing else in the app throws an object with
 * a numeric `status`, so the narrowing costs nothing.
 *
 * <b>Two 403s are deliberately excluded, and both would do damage.</b> `acting_role_not_held` is
 * ADR 0009's refusal to take a stale role choice, and `App.tsx` already answers it by clearing the
 * choice and asking again — a second handler tearing the same session down would fight it, and the
 * player would be told a room revoked a card when what really happened is that a room assignment
 * was withdrawn. `room_not_operated` is staff refused a room they do not run; it says nothing
 * about anybody's card, and acting on it would end an operator's browse session.
 */
export function triggersMembershipRecheck(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;

  const { status, code } = error as { status?: unknown; code?: unknown };
  if (status === 404) return true;
  if (status !== 403) return false;

  // The policy's own refusal carries no envelope, so an absent code is the signal rather than the
  // absence of one. Any *other* code is a refusal that knows what it is about and is not this.
  if (code === undefined || code === null || code === '') return true;

  return typeof code === 'string' && MEMBERSHIP_DENIAL_CODES.includes(code);
}

// ------------------------------------------------------------------------- the branch

/**
 * Which card's portal is on screen, given the stored preference.
 *
 * Lifted out of `PlayerProvider` so that `revokeOutcome` can ask it about the membership list
 * *before* a revoke and the list *after* one — the two answers are what decide whether anything
 * the player is looking at actually moved. Two implementations of this would disagree in the one
 * direction that matters: the branch would call a swap quiet, and the swap would then happen.
 *
 * A stored room that names nothing this account still holds resolves to null rather than to an
 * error: an assignment can end, a room can revoke, and a preference that outlived its card must
 * not be able to open the app on nothing. With exactly one card the preference is not consulted at
 * all — one card is not a choice, and asking would be furniture.
 */
export function resolveMembership<T extends CardAtRoom>(
  cards: readonly T[],
  chosenRoomId: string | null,
): T | null {
  const stored = cards.find((card) => card.roomId === chosenRoomId) ?? null;
  return stored ?? (cards.length === 1 ? cards[0] : null);
}

/** What a revoke discovered mid-session should do to the screen. */
export type RevokeOutcome =
  /** Nothing this sign-in holds went away — so nothing happened, and nothing may be claimed. */
  | { readonly kind: 'unchanged' }
  /** A card went, but not one the screen was resolved to. Refresh the list and say nothing. */
  | { readonly kind: 'quiet' }
  /** The subject of the page moved. Stop, show the cards list, and say what happened. */
  | ({ readonly kind: 'announce' } & RevokedCard);

/**
 * CR4's branch, decided by comparing the two membership lists rather than by matching a room id.
 *
 * <b>The trap this exists to close.</b> `resolveMembership` falls back to the sole remaining card
 * when the stored preference resolves to nothing. So a player holding two cards, reading room A's
 * standings, whose card at A is revoked, is *silently moved into room B's portal* the moment
 * `/api/me` is refetched — which is the very refetch this fix adds. CR4 forbids exactly that:
 * "Teleporting somebody from one room's standings into another's, with no explanation, is worse
 * than the stale state being fixed."
 *
 * <b>So the test is "did the subject of the page move", not "was the revoked room the active
 * one".</b> Those look equivalent and are not. A player sitting on the cards list with two cards
 * has no active room at all — `chosenRoomId` resolves to nothing, which is why the list is showing
 * — and losing one of them drops the fallback to a single card and opens that room's portal
 * underneath them. A room-id comparison calls that quiet; it is the same teleport arriving from
 * the other direction. CR4's fourth bullet justifies quiet by "nothing on screen is affected", and
 * this is the literal reading of that sentence.
 *
 * <b>`unchanged` is separate from `quiet` on purpose.</b> A membership 403 need not be a revoke —
 * a request can name a room this sign-in never held, and the bare policy refusal names no room at
 * all — so a refetch that finds the list intact must leave the screen alone rather than report a
 * disconnection that did not happen.
 */
export function revokeOutcome<T extends CardAtRoom>({
  before,
  after,
  chosenRoomId,
}: {
  readonly before: readonly T[];
  readonly after: readonly T[];
  readonly chosenRoomId: string | null;
}): RevokeOutcome {
  const held = new Set(after.map((card) => card.roomId));
  const gone = before.filter((card) => !held.has(card.roomId));
  if (gone.length === 0) return { kind: 'unchanged' };

  const wasOn = resolveMembership(before, chosenRoomId);
  const nowOn = resolveMembership(after, chosenRoomId);
  if ((wasOn?.roomId ?? null) === (nowOn?.roomId ?? null)) return { kind: 'quiet' };

  // Name the card the reader was actually on where that is the one that went; otherwise the first
  // that did. The second case is the cards-list one above, where there was no card on screen to
  // name — and losing two at once is rare enough that listing them would cost more wording than
  // it buys.
  const named = gone.find((card) => card.roomId === wasOn?.roomId) ?? gone[0];

  return {
    kind: 'announce',
    roomId: named.roomId,
    roomName: named.roomName,
    othersRemain: after.length > 0,
  };
}

/**
 * What the player is told, once, when a card is taken away under them.
 *
 * <b>Half of this sentence is about what did *not* happen, and that is CR4's fifth bullet rather
 * than reassurance.</b> Part F5 of the walk proved a fantasy team survives a revoke — the row is
 * keyed on the card and not on the sign-in (ADR 0010, Decision 7) — so the player has lost access
 * and not history, and connecting the card again returns it. A notice saying only "your card was
 * disconnected" would be read as a deletion by everybody holding a lineup in a running contest,
 * which is precisely the population this reaches.
 *
 * <b>It says what happened and not why.</b> A revoke is the room's decision, taken at the room's
 * desk, and this app has no route to the reason — the same call `amendedNotice` makes about §6.2's
 * event list. Nor does it read as an error: a room withdrawing a card is an ordinary act, and
 * styling it as a fault would send somebody to support over a decision somebody made on purpose.
 */
export function revokedCardNotice(revoked: RevokedCard): string {
  return (
    `Your card at ${revoked.roomName} was disconnected by the room, so this sign-in can no ` +
    'longer open its portal. Nothing was deleted — your results, standings and any fantasy ' +
    'teams there are exactly as you left them, and connecting the card again restores access ' +
    'to them.' +
    (revoked.othersRemain ? ' Your other cards are unaffected.' : '')
  );
}

// ------------------------------------------------------------------------- the latch

/** How long a finished check absorbs further triggers. */
export const RECHECK_COOLDOWN_MS = 10_000;

/** Where the recheck of `/api/me` stands. */
export interface RecheckLatch {
  /** A refetch is out and its answer has not arrived. */
  readonly inFlight: boolean;
  /** When the last successful check finished, or null if none has. */
  readonly lastFinishedAt: number | null;
}

/**
 * Whether a trigger should start a recheck of `/api/me`.
 *
 * <b>The latch is time, never the membership list.</b> It was the list's identity: a check
 * remembered the list it ran on and skipped any trigger while that list was unchanged. That reads
 * as "a second revoke is noticed on its own merits", and it is the reverse — a check that finds
 * nothing gone leaves the list unchanged (TanStack's structural sharing keeps the same reference),
 * so every later trigger compared equal and was skipped for the rest of the session. One refusal
 * that was not a revoke — a room this sign-in never held, a contest somebody deleted — switched
 * revoke detection off until a reload, and the wider trigger below would make that the ordinary
 * case rather than a rare one.
 *
 * <b>So: never while a check is out, and not within {@link RECHECK_COOLDOWN_MS} of the last one
 * finishing.</b> A screen opening four queries produces four failures in a burst; the first starts
 * the check, the rest arrive while it is in flight or just after, and cost nothing. A failure ten
 * seconds later is a new question and gets a new answer. A check that failed is recorded with no
 * finish time, so a transient network error never holds the next one back.
 */
export function shouldRecheck(latch: RecheckLatch, now: number): boolean {
  if (latch.inFlight) return false;
  return latch.lastFinishedAt === null || now - latch.lastFinishedAt >= RECHECK_COOLDOWN_MS;
}

// ------------------------------------------------------------------- getting it there

/**
 * The bridge between the fetch layer and `PlayerProvider`, and the reason it is a module-level
 * notifier rather than a hook.
 *
 * A recheck trigger can come from almost anything this app asks for — the roster, standings, a
 * contest, a fantasy team upsert — so the observation point has to be the one place every failed
 * request already passes through, which is the query client's two caches in `main.tsx`. That
 * client is built once, at module scope, outside React, and it therefore has no `setState` to
 * call. This is the seam.
 *
 * <b>`main.tsx` reports every failure and knows none of the rules.</b> The predicate lives here,
 * beside the branch it feeds, so there is one place that decides what a membership denial is.
 */
type DenialListener = () => void;

const listeners = new Set<DenialListener>();

/** Called for every failed query and mutation in the app. Most are none of our business. */
export function reportRequestFailure(error: unknown): void {
  if (!triggersMembershipRecheck(error)) return;
  // Copied before iterating: a listener that unsubscribes itself while we are walking the set
  // would otherwise mutate it mid-iteration.
  for (const listener of [...listeners]) listener();
}

/** Subscribes to recheck triggers. Returns the unsubscribe, for an effect to return directly. */
export function onMembershipRecheck(listener: DenialListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
